import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import SnapshotDPPViewer from './SnapshotDPPViewer';
import type { PublishedSnapshot } from '../types/snapshot';

/**
 * Handles GS1 Digital Link paths: /01/{GTIN}/10/{BATCH}/21/{SERIAL}/...
 */
const Gs1ResolverHandler: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const resolve = async () => {
      const gs1Path = location.pathname;
      if (!gs1Path.startsWith('/01/')) {
        setError('Invalid GS1 Digital Link path');
        setLoading(false);
        return;
      }

      try {
        const response = await apiService.publicResolver.resolve(gs1Path);
        const data = response.data?.data;
        const redirect = data?.redirect_url as string | undefined;
        const snap = data?.snapshot as Record<string, unknown> | undefined;

        if (snap?.uniqueIdentifier || snap?.unique_identifier) {
          setSnapshot(snap);
          setLoading(false);
          return;
        }

        if (redirect) {
          try {
            const url = new URL(redirect);
            const dppMatch = url.pathname.match(/\/dpp\/([^/]+)/);
            if (dppMatch?.[1] && url.host === window.location.host) {
              navigate(`/dpp/${encodeURIComponent(dppMatch[1])}`, { replace: true });
              return;
            }
          } catch {
            // relative or external URL
          }
          window.location.replace(redirect);
          return;
        }

        setError('No destination resolved for this GS1 link');
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { error?: string } } };
        setError(axiosErr.response?.data?.error || 'Failed to resolve GS1 Digital Link');
      } finally {
        setLoading(false);
      }
    };

    resolve();
  }, [location.pathname, navigate]);

  if (loading) {
    return <div className="spinner"><div className="spinner-inner"></div></div>;
  }

  if (snapshot) {
    return <SnapshotDPPViewer snapshot={snapshot as unknown as PublishedSnapshot} />;
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">
          <h2>Resolver Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return null;
};

export default Gs1ResolverHandler;
